/**
 * The tag shop's product TYPE axis — Shopify's per-product `Type` field, surfaced by
 * `/shop/tags/products` as `category_type` (`node.productType` in
 * `get-tag-shop-products.use-case.ts`).
 *
 * Verified against the live store (kbw***.myshopify.com, 17 products): `productType` takes
 * exactly two values, `"Tag"` and `"Hardware"`. Every tag form factor is `Tag`; the only
 * `Hardware` product is the **Vera Tag Desk Reader**. So "filter the shop to readers" is
 * `category_type === 'Hardware'` — a real Shopify field, not a title match.
 *
 * WHY A SHARED MODULE. `TagsShopProductPreview` already hand-rolled the hardware test
 * inline (VERA-246, so a reader card shows its description instead of Best-for chips), and
 * this change needed the same test in five more places — the shop's type filter and the four
 * "pick a tag type" selectors that must NOT offer a desk reader as a tag. That is the second
 * hand-roll, so it becomes one predicate (working-conventions §36).
 *
 * The predicate is case-insensitive and null-safe on purpose: the API's DEV fallback catalog
 * (`_mock/tag-shop-mock.ts`) returns `category_type: null`, and the platform's own mock uses
 * the plural `'Tags'`. A strict `=== 'Tag'` test would empty those selectors whenever Shopify
 * is unreachable; testing "is it hardware?" degrades safely instead.
 */

export const SHOP_PRODUCT_TYPE = {
  TAG: 'Tag',
  HARDWARE: 'Hardware',
} as const

export type ShopProductType =
  (typeof SHOP_PRODUCT_TYPE)[keyof typeof SHOP_PRODUCT_TYPE]

/** Query-string key the shop reads its type filter from (`/tags/shop?type=Hardware`). */
export const SHOP_TYPE_PARAM = 'type'

/**
 * Buyer-facing name for a type. The Shopify value is warehouse vocabulary; a shopper is
 * looking for "Readers", not "Hardware". Falls back to the raw value for any type added to
 * the store later, so a new type is unlabelled rather than invisible.
 */
export const SHOP_TYPE_LABEL: Record<string, string> = {
  [SHOP_PRODUCT_TYPE.HARDWARE]: 'Readers',
  [SHOP_PRODUCT_TYPE.TAG]: 'Tags',
}

export const shopTypeLabel = (type: string): string =>
  SHOP_TYPE_LABEL[type] ?? type

/** Normalised comparison — see the module note on why this is not `=== 'Hardware'`. */
export const isShopProductType = (
  categoryType: string | null | undefined,
  type: ShopProductType,
): boolean =>
  String(categoryType ?? '').trim().toLowerCase() === type.toLowerCase()

/**
 * True for the desk reader (and any future non-tag hardware). Use this — never a title
 * match — wherever a surface must treat a reader differently from a tag.
 */
export const isHardwareProduct = (product: {
  category_type?: string | null
}): boolean => isShopProductType(product?.category_type, SHOP_PRODUCT_TYPE.HARDWARE)

/**
 * True for anything a *tag* selector may offer: every tag form factor, plus anything whose
 * type is unknown (mock/degraded data). Excludes hardware only.
 */
export const isTagProduct = (product: {category_type?: string | null}): boolean =>
  !isHardwareProduct(product)
